<script lang="ts">
import { defineComponent } from 'vue';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import CButtonIcon from '@/components/shared/CButtonIcon.vue';
import CDropdown from '@/components/shared/CDropdown.vue';
import { DropdownPlacement } from '@/enums/DropdownPlacement.enum';
import { ButtonIconType } from '@/enums/ButtonIconType.enum';
import { ButtonIconColor } from '@/enums/ButtonIconColor.enum';
import { MapView } from '@/enums/MapView.enum';
import { t } from '@/utils/utils';

enum ComponentEvent {
	CHANGE_VIEW = 'changeView',
}

interface MapViewOption {
	value: MapView;
	label: string;
}

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'MapViewSelector',
	components: { CButtonIcon, CDropdown },
	props: {
		currentView: {
			required: true,
			type: String as () => MapView,
			default: MapView.NONE,
		},
		buttonIconType: {
			required: false,
			type: String as () => ButtonIconType,
			default: () => ButtonIconType.RAISED,
		},
		buttonIconColor: {
			required: false,
			type: String as () => ButtonIconColor,
			default: () => ButtonIconColor.BASIC,
		},
		hideAnimation: {
			required: false,
			type: Boolean,
			default: false,
		},
		placement: {
			required: false,
			type: String as () => DropdownPlacement,
			default: () => DropdownPlacement.BOTTOM,
		},
		shadowRoot: {
			required: true,
			type: [ShadowRoot, null],
		},
	},
	emits: [ComponentEvent.CHANGE_VIEW],
	setup() {return {
			ComponentEvent,
			faLayerGroup,
			DropdownPlacement,
		};
	},
	data() {
		return {
			dropdownTriggerActive: false,
		};
	},
  computed: {
		viewOptions(): MapViewOption[] {
			return [
				{ value: MapView.NONE, label: t('mapView.noneValue')},
				{ value: MapView.TERRAIN, label: t('mapView.terrain')},
				{ value: MapView.SATELLITE, label: t('mapView.satellite')},
			];
		},
	},
});
</script>

<template>
	<c-dropdown
		:placement="placement"
		:hide-animation="hideAnimation"
		:shadow-root="shadowRoot"
		@change-visibility="dropdownTriggerActive = $event"
	>
		<template #trigger="{ toggleVisibility }">
			<c-button-icon
				:type="buttonIconType"
				:color="buttonIconColor"
				:icon-name="faLayerGroup.iconName"
				:is-active="dropdownTriggerActive"
				@click="toggleVisibility()"
			/>
		</template>
		<template #content="{ toggleVisibility }">
			<ul class="map-view-selector__list">
				<li
					v-for="option in viewOptions"
					:key="option.value"
					class="map-view-selector__option"
					:class="{ 'map-view-selector__option--active': option.value === currentView }"
					@click="
						$emit(ComponentEvent.CHANGE_VIEW, option.value);
						toggleVisibility();
					"
				>
					<span class="map-view-selector__option-label">{{ option.label }}</span>
				</li>
			</ul>
		</template>
	</c-dropdown>
</template>

<style scoped lang="scss">
.map-view-selector__list {
	> li {
		min-width: 120px;
		cursor: pointer;
		padding: 6px 16px;

		&:hover {
			background-color: tokens.$color-gray-f5;
		}

    &.map-view-selector__option--active {
      background-color: #212529;
      color: white;
    }
	}
}
</style>
